import { ReactNode, createContext, useContext } from 'react'
import { Workflow, WorkflowState } from '@/types/workflows'
import { useGetWorkflows } from '@/service-library/hooks/workflows'
import { sortBySortOrder } from '@/utils/field'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'

type WorkflowContextValue = ReturnType<typeof useGetWorkflows> & {
  workflows: Workflow[]
  productionDataWorkflow: Workflow | undefined
  labelTrainingWorkflow: Workflow | undefined
  getWorkflowByCode: (code: string) => Workflow | undefined
  getWorkflowStateByCode: (
    workflowCode: string,
    stateCode: string,
  ) => WorkflowState | undefined
  getAllWorkflowStates: () => WorkflowState[]
  getWorkflowsByModelType: () => {
    productionWorkflows: Workflow[]
    labelingModelWorkflows: Workflow[]
    categoryModelWorkflows: Workflow[]
  }
}

const WorkflowContext = createContext<WorkflowContextValue>({
  workflows: [],
  isPending: false,
  productionDataWorkflow: undefined,
  labelTrainingWorkflow: undefined,
  getWorkflowByCode: () => undefined,
  getWorkflowStateByCode: () => undefined,
  getAllWorkflowStates: () => undefined,
  getWorkflowsByModelType: () => ({
    productionWorkflows: [],
    labelingModelWorkflows: [],
    categoryModelWorkflows: [],
  }),
  getChildWorkflows: () => [],
} as unknown as WorkflowContextValue)

type WorkflowsProviderProps = {
  children: ReactNode
}

export const useWorkflowsContext = () => useContext(WorkflowContext)

export default function WorkflowsProvider({
  children,
}: WorkflowsProviderProps) {
  const { project } = useProjectContext()
  const query = useGetWorkflows({
    filters: {
      limit: '1000',
      project_id: project.id,
      fields__include: 'workflow_states',
      workflow_states__fields__include:
        'workflow_id,excluded_project_grid_fields_ids,workflow_state_type',
    },
  })

  const { workflows } = query

  const workflowsWithOrderedStates = workflows.map((workflow) => ({
    ...workflow,
    workflow_states: sortBySortOrder(workflow?.workflow_states || []),
  }))

  function getFirstNonModelWorkflow() {
    return workflows.find((workflow) => !workflow.project_model_id)
  }

  // Note: When there are multiple workflows with the same code (e.g. 'categorization') this will return the first one
  function getWorkflowByCode(code: string) {
    return workflows.find((workflow) => workflow.code === code)
  }

  function getWorkflowStateByCode(workflowCode: string, stateCode: string) {
    const workflow = getWorkflowByCode(workflowCode)
    return workflow?.workflow_states?.find((state) => state.code === stateCode)
  }

  function getAllWorkflowStates() {
    return workflows.flatMap((workflow) => workflow.workflow_states || [])
  }

  function getWorkflowsByModelType(): {
    productionWorkflows: Workflow[]
    labelingModelWorkflows: Workflow[]
    categoryModelWorkflows: Workflow[]
  } {
    return workflows.reduce<{
      productionWorkflows: Workflow[]
      labelingModelWorkflows: Workflow[]
      categoryModelWorkflows: Workflow[]
    }>(
      (acc, workflow) => {
        switch (workflow.code) {
          case 'labeling':
            acc.labelingModelWorkflows.push(workflow)
            break
          case 'categorization':
            acc.categoryModelWorkflows.push(workflow)
            break
          default:
            acc.productionWorkflows.push(workflow)
            break
        }
        return acc
      },
      {
        productionWorkflows: [],
        labelingModelWorkflows: [],
        categoryModelWorkflows: [],
      },
    )
  }

  return (
    <WorkflowContext.Provider
      value={{
        ...query,
        workflows: workflowsWithOrderedStates,
        getWorkflowByCode,
        getWorkflowStateByCode,
        getAllWorkflowStates,
        productionDataWorkflow: getFirstNonModelWorkflow(),
        labelTrainingWorkflow: getWorkflowByCode('labeling'),
        getWorkflowsByModelType,
      }}
    >
      {children}
    </WorkflowContext.Provider>
  )
}
